import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Platform Statistics Service - Fetches real-time platform metrics for public display
 */
const platformStatsService = {
  /**
   * Get platform-wide statistics
   * @returns {Promise<Object>} Platform statistics
   */
  getPlatformStats: async () => {
    try {
      // Fetch all users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const users = [];
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          id: doc.id,
          userType: userData.userType,
          createdAt: userData.createdAt?.toDate?.() || userData.createdAt,
        });
      });
      
      // Fetch all campaigns
      const campaignsRef = collection(db, 'campaigns');
      const campaignsSnapshot = await getDocs(campaignsRef);
      const campaigns = [];
      
      campaignsSnapshot.forEach((doc) => {
        const campaignData = doc.data();
        campaigns.push({
          id: doc.id,
          status: campaignData.status,
          createdAt: campaignData.createdAt?.toDate?.() || campaignData.createdAt,
        });
      });
      
      // Fetch all reviews/submissions
      const reviewsRef = collection(db, 'reviews');
      const reviewsSnapshot = await getDocs(reviewsRef);
      const reviews = reviewsSnapshot.size;
      
      // Calculate statistics
      const totalBrands = users.filter(user => user.userType === 'brand').length;
      const totalInfluencers = users.filter(user => user.userType === 'influencer').length;
      const totalCampaigns = campaigns.length;
      const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
      const completedCampaigns = campaigns.filter(c => c.status === 'completed').length;
      
      // Calculate growth trends (last 30 days vs previous 30 days)
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
      
      const recentBrands = users.filter(u => 
        u.userType === 'brand' && 
        u.createdAt && 
        new Date(u.createdAt) > thirtyDaysAgo
      ).length;
      
      const previousBrands = users.filter(u => 
        u.userType === 'brand' && 
        u.createdAt && 
        new Date(u.createdAt) > sixtyDaysAgo && 
        new Date(u.createdAt) <= thirtyDaysAgo
      ).length;
      
      const recentInfluencers = users.filter(u => 
        u.userType === 'influencer' && 
        u.createdAt && 
        new Date(u.createdAt) > thirtyDaysAgo
      ).length;
      
      const previousInfluencers = users.filter(u => 
        u.userType === 'influencer' && 
        u.createdAt && 
        new Date(u.createdAt) > sixtyDaysAgo && 
        new Date(u.createdAt) <= thirtyDaysAgo
      ).length;
      
      const recentCampaigns = campaigns.filter(c => 
        c.createdAt && 
        new Date(c.createdAt) > thirtyDaysAgo
      ).length;
      
      const previousCampaigns = campaigns.filter(c => 
        c.createdAt && 
        new Date(c.createdAt) > sixtyDaysAgo && 
        new Date(c.createdAt) <= thirtyDaysAgo
      ).length;
      
      // Calculate growth percentages
      const brandsGrowth = previousBrands > 0 
        ? Math.round(((recentBrands - previousBrands) / previousBrands) * 100) 
        : recentBrands > 0 ? 100 : 0;
      
      const influencersGrowth = previousInfluencers > 0 
        ? Math.round(((recentInfluencers - previousInfluencers) / previousInfluencers) * 100) 
        : recentInfluencers > 0 ? 100 : 0;
      
      const campaignsGrowth = previousCampaigns > 0 
        ? Math.round(((recentCampaigns - previousCampaigns) / previousCampaigns) * 100) 
        : recentCampaigns > 0 ? 100 : 0;
      
      return {
        totalBrands,
        totalInfluencers,
        totalCampaigns,
        activeCampaigns,
        completedCampaigns,
        totalReviews: reviews,
        totalUsers: users.length,
        growth: {
          brands: brandsGrowth,
          influencers: influencersGrowth,
          campaigns: campaignsGrowth,
        },
        recentActivity: {
          brands: recentBrands,
          influencers: recentInfluencers,
          campaigns: recentCampaigns,
        }
      };
    } catch (error) {
      console.error('Error fetching platform stats:', error);
      throw error;
    }
  },
  
  /**
   * Get monthly growth data for charts (last 6 months)
   * @returns {Promise<Object>} Monthly growth data
   */
  getMonthlyGrowth: async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const campaignsSnapshot = await getDocs(collection(db, 'campaigns'));
      
      const now = new Date();
      const monthlyData = [];
      
      // Generate data for last 6 months
      for (let i = 5; i >= 0; i--) {
        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonthDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
        
        const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });
        
        let brandsCount = 0;
        let influencersCount = 0;
        let campaignsCount = 0;
        
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          const createdAt = userData.createdAt?.toDate?.() || userData.createdAt;
          
          if (createdAt && createdAt >= monthDate && createdAt < nextMonthDate) {
            if (userData.userType === 'brand') brandsCount++;
            if (userData.userType === 'influencer') influencersCount++;
          }
        });
        
        campaignsSnapshot.forEach((doc) => {
          const campaignData = doc.data();
          const createdAt = campaignData.createdAt?.toDate?.() || campaignData.createdAt;
          
          if (createdAt && createdAt >= monthDate && createdAt < nextMonthDate) {
            campaignsCount++;
          }
        });
        
        monthlyData.push({
          month: monthName,
          brands: brandsCount,
          influencers: influencersCount,
          campaigns: campaignsCount,
        });
      }
      
      return monthlyData;
    } catch (error) {
      console.error('Error fetching monthly growth:', error);
      throw error;
    }
  }
};

export default platformStatsService;
